import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import DashboardSkeleton from "../../common/DashboardSkeleton";
import overdue from "../../assets/overdue.jpg";
import working from "../../assets/working.jpg";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import formatDateAndTime from "../../common/formatDateAndTime";

const Runningrepair = ({ setSavedAmount, cityFilter }) => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  let token = localStorage.getItem("token");
  const [runningRepairData, setRunningRepairData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchText, setSearchText] = useState("");
  const rowsPerPage = 10;

  const handleCopy = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      toast.success(`${label} copied to clipboard`);
    } catch (err) {
      toast.error(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  const getRunningRepairData = async () => {
    if (!token) {
      return toast.error("Session Expired Close all the tab and signin again");
    }
    setLoading(true);
    let query = "";
    if (cityFilter && cityFilter !== "All Cities") {
      query += `&cityId=${cityFilter}`;
    }
    try {
      const response = await fetch(
        `${apiUrl}/api/dashboardreports/running-repair?${query}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        },
      );
      const data = await response.json();
      if (data?.statusCode === "0100") {
        setLoading(false);
        setRunningRepairData(data?.dataValues);
        setCurrentPage(1);
        setSavedAmount(data?.dataValues?.metricsSummary?.totalAmountSaved);
      } else {
        setLoading(false);
        toast.error(data?.msg);
      }
      return data;
    } catch (error) {
      setLoading(false);
      console.error("Error fetching Hub Names:", error);
    }
  };

  useEffect(() => {
    getRunningRepairData();
  }, [cityFilter]);

  const runningRepairOverviewData = {
    counts: [
      {
        title: "TOTAL RUNNING REPAIR COST",
        value: runningRepairData?.metricsSummary?.totalFinancialsSum
          ? `₹ ${runningRepairData.metricsSummary.totalFinancialsSum}`
          : "₹ 0",
        icon: (
          <span className="text-slate-400 font-medium text-sm select-none">
            $
          </span>
        ),
        outof: "Running repairs all vehicles",
        topBar: "bg-[#1d4ed8]",
      },
      {
        title: "RUNNING REPAIRS ORDERS",
        value: runningRepairData?.metricsSummary?.statusCounts?.pending ?? 0,
        icon: (
          <svg
            className="w-3.5 h-3.5 text-slate-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="m9 12 2 2 4-4" />
          </svg>
        ),
        outof: "pending",
        topBar: "bg-[#16a34a]",
      },
      {
        title: "TOTAL COUNT AVG",
        value: runningRepairData?.metricsSummary?.totalCountAverage ?? "0",
        icon: null,
        outof: null,
        topBar: "bg-[#64748b]",
      },
      {
        title: "AVG/ VEHICLE",
        value: runningRepairData?.metricsSummary?.totalCostAverage
          ? `₹ ${runningRepairData.metricsSummary.totalCostAverage}`
          : "₹ 0",
        icon: null,
        outof: null,
        topBar: "bg-[#d97706]",
      },
    ],
  };

  const amountOverviewData = {
    counts: [
      {
        title: "COUNT",
        value: runningRepairData?.metricsSummary?.statusCounts?.total ?? 0,
        outof: "Total repair orders",
        topBar: "bg-[#22c55e]",
      },
      {
        title: "AMOUNT",
        value: runningRepairData?.metricsSummary?.totalFinancialsSum
          ? `₹${runningRepairData.metricsSummary.totalFinancialsSum}`
          : "₹ 0",
        outof: "Total repair cost",
        topBar: "bg-[#a855f7]",
      },
      {
        title: "PAYMENT COMPLETED",
        value:
          runningRepairData?.metricsSummary?.totalCostToCompany != null
            ? `₹${
                (runningRepairData.metricsSummary.totalCostToCompany || 0) -
                (runningRepairData.metricsSummary.totalOutstandingBalance || 0)
              }`
            : "₹ 0",
        outof: "Paid invoices",
        topBar: "bg-[#22c55e]",
      },
      {
        title: "PAYMENT PENDING",
        value:
          runningRepairData?.metricsSummary?.totalOutstandingBalance != null
            ? `₹${Number(runningRepairData.metricsSummary.totalOutstandingBalance).toFixed(2)}`
            : "₹ 0",
        outof: "Unpaid invoices",
        topBar: "bg-[#f59e0b]",
      },
    ],
  };

  const filteredRows = (runningRepairData?.serviceList?.rows || []).filter(
    (data) => {
      const matchesFilter =
        statusFilter === "All" ||
        (data?.repairType &&
          String(data.repairType)
            .toLowerCase()
            .includes(statusFilter.toLowerCase())) ||
        (data?.description &&
          String(data.description)
            .toLowerCase()
            .includes(statusFilter.toLowerCase()));

      const searchValue = searchText.trim().toLowerCase();
      const matchesSearch =
        !searchValue ||
        [data?.serviceCode, data?.licensePlate]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(searchValue));

      return matchesFilter && matchesSearch;
    },
  );
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedRows = filteredRows.slice(
    startIndex,
    startIndex + rowsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, statusFilter]);

  const handleDownload = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  return (
    <>
      {loading ? (
        <DashboardSkeleton />
      ) : (
        <div className="w-full flex flex-col gap-6 font-['Inter',sans-serif]">
          {/* Top Row: Large Metric Cards */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {runningRepairOverviewData.counts.map((item, index) => (
              <div
                key={index}
                className="relative bg-white rounded-xl border border-slate-200 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] p-5 pt-4 flex flex-col justify-between h-[126px] overflow-hidden"
              >
                <div
                  className={`w-full h-[3.5px] left-3 top-0 absolute ${item.topBar} rounded-b-md`}
                />
                <div className="flex justify-between items-center w-full mt-1">
                  <span className="text-[#64748b] text-[11px] font-bold tracking-wider uppercase">
                    {item.title}
                  </span>
                  {item.icon && <div className="leading-none">{item.icon}</div>}
                </div>
                <div>
                  <div className="text-slate-900 text-[26px] font-bold tracking-tight leading-none mb-1.5">
                    {item.value}
                  </div>
                  {item.outof ? (
                    <div className="text-[#94a3b8] text-[11.5px] font-normal leading-4">
                      {item.outof}
                    </div>
                  ) : (
                    <div className="h-4" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Middle Row: Slim Status Cards */}
          <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {amountOverviewData.counts.map((item, index) => (
              <div
                key={index}
                className="relative bg-white rounded-2xl border border-slate-200 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.04)] px-5 py-4 flex flex-col justify-center overflow-hidden"
              >
                <div
                  className={`w-full h-[2.5px] left-0 top-0 absolute ${item.topBar}`}
                />
                <span className="text-[#64748b] text-[10px] font-bold tracking-wider uppercase">
                  {item.title}
                </span>
                <div className="text-slate-900 text-[21px] font-bold my-0.5 leading-snug">
                  {item.value}
                </div>
                <span className="text-[#94a3b8] text-[11px] font-normal leading-tight">
                  {item.outof}
                </span>
              </div>
            ))}
          </div>

          {/* Table Container Card */}
          <div className="w-full bg-white rounded-2xl border border-slate-200 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.05)] overflow-hidden">
            {/* Table Toolbar */}
            <div className="px-6 py-5 flex justify-between items-center gap-4 flex-wrap">
              <span className="text-slate-900 text-[13px] font-bold tracking-wide uppercase">
                RUNNING REPAIRS — VEHICLE WISE
              </span>

              <div className="flex items-center gap-3 flex-wrap">
                {/* Search Bar */}
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search vehicle no or work order id"
                  className="px-3.5 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-600 transition-colors w-48 sm:w-56"
                />

                {/* Filter Pills */}
                <div className="p-1 bg-[#f1f5f9] rounded-lg inline-flex items-center gap-0.5">                  
                  <div
                    title="Download Full Report"
                    className="cursor-pointer"
                    onClick={() => {
                      handleDownload(runningRepairData?.reportUrl);
                    }}
                  >
                    <DownloadForOfflineIcon />
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="w-full overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-t border-b border-slate-200/80 bg-white">
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                      CODE
                    </th>
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                      REGISTRATION
                    </th>                                       
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                      CREATED DATE
                    </th>
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                      BLOCK START DATE
                    </th>
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                      BLOCK END DATE
                    </th>
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                      PAYMENT STATUS
                    </th>
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                      OUTSTANDING BALANCE
                    </th>
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                      COST TO COMPANY
                    </th>                    
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                      BILLING AMOUNT
                    </th>                    
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                     STARTING ODOMETER
                    </th>
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap">
                     ENDING ODOMETER
                    </th>
                    <th className="px-6 py-3.5 text-[#94a3b8] text-[10.5px] font-semibold tracking-wider uppercase whitespace-nowrap text-right">
                      IS CREDIT APPLIED
                    </th>                   
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((data, index) => {
                      const displayRepair =
                        data?.repairType || "Running repair";
                      return (
                        <tr
                          key={`${data?.serviceCode || index}-${index}`}
                          className="hover:bg-slate-50/50 transition-colors"
                        >
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap">
                            <div className="inline-flex items-center gap-1.5">
                              <span>{data?.serviceCode || "-"}</span>
                              {data?.serviceCode && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleCopy(data?.serviceCode, "Code")
                                  }
                                  title="Copy Code"
                                  className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <rect
                                      width="14"
                                      height="14"
                                      x="8"
                                      y="8"
                                      rx="2"
                                      ry="2"
                                    />
                                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-[12px] font-bold text-slate-900 whitespace-nowrap">
                            <div className="inline-flex items-center gap-1.5">
                              <span>{data?.licensePlate || "-"}</span>
                              {data?.licensePlate && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleCopy(
                                      data?.licensePlate,
                                      "Registration",
                                    )
                                  }
                                  title="Copy Registration"
                                  className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                >
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="13"
                                    height="13"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <rect
                                      width="14"
                                      height="14"
                                      x="8"
                                      y="8"
                                      rx="2"
                                      ry="2"
                                    />
                                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>                          
                        
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap">
                            {formatDateAndTime(data?.createdAt) || "-"}
                          </td>
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap">
                            {formatDateAndTime(data?.blockedDetails?.startDate) || "-"}
                          </td>
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap">
                            {formatDateAndTime(data?.blockedDetails?.endDate) || "-"}
                          </td>
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap">
                            {data?.isPaymentPending === false ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-green-50 border border-green-200 text-green-600 text-xs font-semibold whitespace-nowrap">
                                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-green-500 text-white text-[10px]">
                                  ✓
                                </span>
                                Completed
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-600 text-xs font-semibold whitespace-nowrap">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap text-slate-900">
                            <span className="text-[11px] font-medium">
                                ₹
                              </span>
                            {data?.outstandingBalance || " 0"}
                          </td>
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap">
                            {data?.billingDetails?.costToCompany != null
                              ? `₹${Number(data.billingDetails.costToCompany).toLocaleString()}`
                              : "-"}
                          </td>
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap">
                            {data?.billingDetails?.billingAmount != null
                              ? `₹ ${Number(data.billingDetails.billingAmount).toLocaleString()}`
                              : "-"}
                          </td>
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap">
                            {data?.odometerReading != null
                              ? Number(data.odometerReading).toLocaleString() + " km"
                              : "-"}
                          </td>
                          <td className="px-6 py-4 text-[12px] font-normal whitespace-nowrap">
                            {data?.endingOdometer != null
                              ? Number(data.endingOdometer).toLocaleString() + " km"
                              : "-"}
                          </td>
                          <td
                            className={`px-6 py-4 text-[12px] font-bold text-right whitespace-nowrap ${
                              data?.billingDetails?.isCreditApplied
                                ? "text-[#16a34a]"
                                : "text-[#dc2626]"
                            }`}
                          >
                            {data?.billingDetails?.isCreditApplied ? "Yes" : "No"}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={10}
                        className="px-6 py-12 text-center text-xs text-slate-400"
                      >
                        No Data Found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-slate-200 px-6 py-4">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs font-medium text-slate-500">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
                className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Runningrepair;
